import { useState, useEffect } from 'react';

const Header = ({ isScrolled, currentPath = '/', onNavigate }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const isWhitepaperPage = currentPath.startsWith('/whitepapers');
    const showDarkToggle = true;

    const handleNavigate = (path) => (event) => {
        if (!onNavigate) {
            return;
        }
        event.preventDefault();
        onNavigate(path);
        setIsMenuOpen(false);
    };

    useEffect(() => {
        if (localStorage.theme === 'dark' ||
            (!('theme' in localStorage) &&
                window.matchMedia('(prefers-color-scheme: dark)').matches)) {
            document.documentElement.classList.add('dark');
            setIsDarkMode(true);
        } else {
            document.documentElement.classList.remove('dark');
            setIsDarkMode(false);
        }
    }, []);

    const toggleDarkMode = () => {
        if (isDarkMode) {
            document.documentElement.classList.remove('dark');
            localStorage.theme = 'light';
            setIsDarkMode(false);
        } else {
            document.documentElement.classList.add('dark');
            localStorage.theme = 'dark';
            setIsDarkMode(true);
        }
    };

    const headerClassName = `fixed w-full z-50 transition-all duration-300 print-hide ${isScrolled ? 'bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm shadow-md' : 'bg-transparent'}`;

    const navLinkClassName =
        'text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors';

    const brandClassName =
        'text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent';

    return (
        <header className={headerClassName}>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center py-4">
                    <a
                        href={isWhitepaperPage ? '/' : '#'}
                        className="flex items-center space-x-2"
                        onClick={isWhitepaperPage ? handleNavigate('/') : undefined}
                    >
                        <span className={brandClassName}>r4j3sh</span>
                    </a>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center space-x-8">
                        {isWhitepaperPage ? (
                            <>
                                <a href="/" className={navLinkClassName} onClick={handleNavigate('/')}>Home</a>
                                <a href="/whitepapers" className={navLinkClassName} onClick={handleNavigate('/whitepapers')}>Whitepapers</a>
                                <a href="/#contact" className={navLinkClassName} onClick={handleNavigate('/#contact')}>Contact</a>
                            </>
                        ) : (
                            <>
                                <a href="#about" className={navLinkClassName}>About</a>
                                <a href="#skills" className={navLinkClassName}>Skills</a>
                                <a href="#projects" className={navLinkClassName}>Projects</a>
                                <a href="#links" className={navLinkClassName}>Links</a>
                                <a href="#contact" className={navLinkClassName}>Contact</a>
                                <a href="/whitepapers" className={navLinkClassName} onClick={handleNavigate('/whitepapers')}>Whitepapers</a>
                            </>
                        )}
                        {showDarkToggle && (
                            <button
                                onClick={toggleDarkMode}
                                className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                aria-label="Toggle dark mode"
                            >
                                {isDarkMode ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                    </svg>
                                )}
                            </button>
                        )}
                    </nav>

                    {/* Mobile Menu Button */}
                    <div className="md:hidden flex items-center">
                        {showDarkToggle && (
                            <button
                                onClick={toggleDarkMode}
                                className="p-2 mr-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                aria-label="Toggle dark mode"
                            >
                                {isDarkMode ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                    </svg>
                                )}
                            </button>
                        )}
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className={`${navLinkClassName} focus:outline-none`}
                            aria-label="Toggle menu"
                        >
                            {!isMenuOpen ? (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>

                {/* Mobile Navigation */}
                {isMenuOpen && (
                    <nav className="md:hidden py-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex flex-col space-y-3">
                            {isWhitepaperPage ? (
                                <>
                                    <a
                                        href="/"
                                        className={`${navLinkClassName} py-2`}
                                        onClick={handleNavigate('/')}
                                    >
                                        Home
                                    </a>
                                    <a
                                        href="/whitepapers"
                                        className={`${navLinkClassName} py-2`}
                                        onClick={handleNavigate('/whitepapers')}
                                    >
                                        Whitepapers
                                    </a>
                                    <a
                                        href="/#contact"
                                        className={`${navLinkClassName} py-2`}
                                        onClick={handleNavigate('/#contact')}
                                    >
                                        Contact
                                    </a>
                                </>
                            ) : (
                                <>
                                    <a
                                        href="#about"
                                        className={`${navLinkClassName} py-2`}
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        About
                                    </a>
                                    <a
                                        href="#skills"
                                        className={`${navLinkClassName} py-2`}
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        Skills
                                    </a>
                                    <a
                                        href="#projects"
                                        className={`${navLinkClassName} py-2`}
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        Projects
                                    </a>
                                    <a
                                        href="#links"
                                        className={`${navLinkClassName} py-2`}
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        Links
                                    </a>
                                    <a
                                        href="#contact"
                                        className={`${navLinkClassName} py-2`}
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        Contact
                                    </a>
                                    <a
                                        href="/whitepapers"
                                        className={`${navLinkClassName} py-2`}
                                        onClick={handleNavigate('/whitepapers')}
                                    >
                                        Whitepapers
                                    </a>
                                </>
                            )}
                        </div>
                    </nav>
                )}
            </div>
        </header>
    );
};

export default Header;
